import { useState, useEffect } from 'react';
import { NudosHoverText, NudosSearchbarDropdown } from '../../../../../../../../components/NudosComponents';
import { searchReferenceProducts } from '../../../../../../../../services/services';
import { IProductReference } from '../../../../../../../../types/services';
import SingleProductCard from './components/SingleProductCard/SingleProductCard';
import { IpillsByReference } from '../../../../../../../../types/orders';
import NudosProductPills from '../../../../../../../../components/NudosComponents/NudosProductPills/NudosProductPills';
import useStockOrdersModuleState from '../../../../../../../../state/useStockOrdersModuleState';
import { formatRoundedUpNumber } from '../../../../../../../../utils/formatNumbers';
import { META_BASE_STOCK_ORDER_URL } from '../../../../../../../../types/stockOrders';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import './SkuProducts.scss';

/**
 * Componente para buscar y seleccionar productos SKU
 * @returns JSX.Element
 */
const SkuProducts = () => {
  const { selectedProducts, setSelectedProducts, getTotalCost, searchWarehouse } = useStockOrdersModuleState();
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState<IProductReference[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        const response = await searchReferenceProducts({ query: searchQuery ?? '', omitCanBeLoaded: true });
        setProducts(response);
      } catch (error) {
        displayErrorNotification({
          customJSXMessage: <>Error buscando productos</>
        });
      } finally {
        setIsLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      fetchProducts();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedProducts]);

  const toggleProduct = (product: IProductReference) => {
    const exists = selectedProducts.some(p => p.id === product.id);
    if (exists) {
      setSelectedProducts(selectedProducts.filter(p => p.id !== product.id));
    } else {
      setSelectedProducts([
        ...selectedProducts,
        {
          ...product,
          quantity: 1,
          unitCost: 0,
          taxCost: 0,
          subtotal: 0
        }
      ]);
    }
  };

  const formDropdownOptions = (options: IProductReference[]) => {
    return options.map(option => {
      const formattedPills =
        typeof option?.pills === 'string' ? (JSON.parse(option?.pills) as IpillsByReference) : option?.pills || {};

      const handleToggle = (e: React.MouseEvent | React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        toggleProduct(option);
      };

      return (
        <div key={option?.id} className="skuProductOptionContainer" onClick={handleToggle}>
          <input
            type="checkbox"
            checked={selectedProducts.some(p => p?.id === option?.id)}
            className="skuProductOptionCheckbox"
            onChange={handleToggle}
            onClick={e => e.stopPropagation()}
          />
          <div>
            <div className="flex items-center gap-8">
              <div className="font-medium">
                <NudosHoverText text={option?.name} charactersLimit={60} onlyIfTruncated maxHoverWidth="300px" />
              </div>
              <NudosProductPills pills={formattedPills} characterLimit={6} />
            </div>
            <div className="text-xs text-gray-500">SKU: {option?.sku}</div>
          </div>
        </div>
      );
    });
  };

  const formSelectedOptionUI = (option: IProductReference) => (
    <div className="flex items-center gap-8">
      <span>{option?.name}</span>
    </div>
  );

  const filterBySearchString = (search: string, options: IProductReference[]) => {
    setSearchQuery(search);
    return options;
  };

  const handleGoToMetabaseSku = () => {
    window.open(META_BASE_STOCK_ORDER_URL, '_blank');
  };

  return (
    <div className="skuProducts">
      <div className="text-nudos-text-color text-sm font-extrabold">Productos (SKUs)</div>
      <div className="skuProductsContainer">
        <div className="px-128">
          <NudosSearchbarDropdown
            label=""
            componentSize="extraLarge"
            formSelectedOptionUICallback={formSelectedOptionUI}
            formDropdownOptionsCallback={formDropdownOptions}
            filterBySearchStringCallback={filterBySearchString}
            customPlaceholder="Busca por código de SKU o modelo"
            currentValue={{
              id: 0,
              name: searchQuery,
              sku: '',
              pills: {}
            }}
            isFilled={!!searchQuery}
            rawOptions={products}
            closesOnChangeSelection
            cleanSearchString={false}
          />
          <div className="searchSkusText">
            <span>Usa Metabase para buscar SKUs por modelo, pills y otros filtros de forma más rápida</span>
            <span className="searchSkusTextLink" onClick={handleGoToMetabaseSku}>
              Buscar SKUs
            </span>
          </div>
        </div>

        <div className="selectedProducts">
          {selectedProducts.map(product => (
            <SingleProductCard
              key={`single-productCard-${product?.id}`}
              product={product}
              removeProductCallback={() => toggleProduct(product)}
            />
          ))}
        </div>
      </div>

      <div className="totalOfSkuProductsQuantity">
        <div className="text-nudos-blue font-extrabold">Total General</div>
        <div className="text-nudos-blue font-extrabold">
          ${formatRoundedUpNumber(getTotalCost(), 2)} {searchWarehouse?.currencyCode}
        </div>
      </div>
    </div>
  );
};

export default SkuProducts;
